import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { useConfirm } from '../../shared/ConfirmContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import BundleFormModal from './components/BundleFormModal';
import BundleListTable from './components/BundleListTable';

const BundleList = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', notes: '' });

  const { data: bundles = [], isLoading: loading } = useQuery({
    queryKey: ['item-bundles-list'],
    queryFn: async () => {
      const response = await api.get('warehouses/api/item-bundles/');
      return response.results || response || [];
    },
  });

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.itembundle') {
      queryClient.invalidateQueries({ queryKey: ['item-bundles-list'] });
    }
  });

  const saveMutation = useMutation({
    mutationFn: () => editing
      ? api.patch(`warehouses/api/item-bundles/${editing.id}/`, formData)
      : api.post('warehouses/api/item-bundles/', formData),
    onSuccess: () => {
      setShowModal(false);
      setSuccess(t('warehouses.bundles.editBundle'));
      setTimeout(() => setSuccess(null), 3000);
      queryClient.invalidateQueries({ queryKey: ['item-bundles-list'] });
    },
    onError: () => {
      setError(t('warehouses.bundles.addError'));
      setTimeout(() => setError(null), 5000);
    },
  });
  const saving = saveMutation.isPending;

  const deleteMutation = useMutation({
    mutationFn: (bundle) => api.delete(`warehouses/api/item-bundles/${bundle.id}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['item-bundles-list'] }),
    onError: () => {
      setError(t('warehouses.bundles.addError'));
      setTimeout(() => setError(null), 5000);
    },
  });

  const openAdd = () => {
    setEditing(null);
    setFormData({ name: '', description: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (bundle) => {
    setEditing(bundle);
    setFormData({
      name: bundle.name,
      description: bundle.description || '',
      notes: bundle.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formData.name.trim()) return;
    saveMutation.mutate();
  };

  const handleDelete = async (bundle) => {
    if (!await confirm(t('warehouses.bundles.confirmDelete', { name: bundle.name }))) return;
    deleteMutation.mutate(bundle);
  };

  const handleFormFieldChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  if (loading) {
    return (
      <PageShell
        pretitle={t('warehouses.overview')}
        title={t('warehouses.bundles.title')}
        description={t('warehouses.bundles.subtitle', 'Review bundle templates and keep their entry structure consistent across the warehouse module.')}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('warehouses.overview')}
      title={t('warehouses.bundles.title')}
      description={t('warehouses.bundles.subtitle', 'Review bundle templates and keep their entry structure consistent across the warehouse module.')}
      actions={
        <Button variant="primary" onClick={openAdd}>
          <IconPlus size={16} className="me-1" />
          {t('warehouses.bundles.newBundle')}
        </Button>
      }
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <PageSection
        title={<>{t('warehouses.bundles.title')}<span className="badge bg-secondary ms-2">{bundles.length}</span></>}
        description={t('warehouses.bundles.listDescription', 'Open bundle details, maintain bundle metadata, and create new composition templates from one list.')}
        bodyClassName="p-0"
      >
        <BundleListTable
          bundles={bundles}
          onCreate={openAdd}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      </PageSection>

      <BundleFormModal
        show={showModal}
        editing={Boolean(editing)}
        formData={formData}
        saving={saving}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        onFieldChange={handleFormFieldChange}
      />
    </PageShell>
  );
};

export default BundleList;
